import type { HelpAnchorId } from '@myrewards/contracts';
import { useSyncExternalStore } from 'react';
import { prefersReducedMotion } from '../design/motion';
import { setHelpShown } from '../ui/HelpShown';
import { coveredTop } from './coveredTop';

/**
 * Help's "Show me" (APP-090) on the page itself. Help only ever names an anchor from the server's
 * registry that the person's roles can see on this page; this side checks the thing is really on
 * the page and visible before it points at it, so nothing hidden or absent is ever highlighted.
 */

const MAIN_ID = 'main';
/** Anchor ids are plain words with hyphens (`pool-card`): nothing else goes into a selector. */
const ANCHOR_ID = /^[a-z][a-z0-9-]*$/;

/** Whether the person can actually see it: laid out, not hidden, inert or hidden from them. */
export function isShowable(element: HTMLElement): boolean {
  if (element.closest('[hidden], [inert], [aria-hidden="true"]') !== null) return false;
  if (typeof element.checkVisibility === 'function')
    return element.checkVisibility({ visibilityProperty: true, opacityProperty: true });
  return element.getClientRects().length > 0;
}

/** The thing on this page Help calls `id`, if it is there and visible; else null. */
export function findHelpAnchor(id: HelpAnchorId): HTMLElement | null {
  if (!ANCHOR_ID.test(id)) return null;
  const main = document.getElementById(MAIN_ID);
  if (main === null) return null;
  for (const element of main.querySelectorAll<HTMLElement>(`[data-help-anchor="${id}"]`))
    if (isShowable(element)) return element;
  return null;
}

function subscribePage(onChange: () => void): () => void {
  const main = document.getElementById(MAIN_ID);
  const observer = new MutationObserver(onChange);
  if (main !== null)
    observer.observe(main, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'inert', 'aria-hidden', 'data-help-anchor'],
    });
  window.addEventListener('resize', onChange);
  return () => {
    observer.disconnect();
    window.removeEventListener('resize', onChange);
  };
}

/** True while the thing is on the page and visible: Help offers "Show me" only then. */
export function useHelpAnchorPresent(id: HelpAnchorId | null): boolean {
  return useSyncExternalStore(
    subscribePage,
    () => id !== null && findHelpAnchor(id) !== null,
    () => false,
  );
}

let release: (() => void) | null = null;

/** Takes the highlight away (focus left it, another was shown, or the page changed). */
export function clearHelpShown(): void {
  release?.();
  release = null;
  setHelpShown(null);
}

/** In the window and clear of the Level 2 bar. */
function inView(rect: DOMRect): boolean {
  return rect.top >= coveredTop() && rect.bottom <= window.innerHeight;
}

/**
 * Where focus goes, so a screen reader says what was shown:
 * - the heading that names it;
 * - else the thing itself when it has a name of its own (an aria-label or aria-labelledby region);
 * - else, for something with no name such as the Export button's wrapper (APP-090 §8), the first
 *   control inside it, which names itself;
 * - else the thing itself.
 */
function focusTargetOf(element: HTMLElement): HTMLElement {
  const id = element.getAttribute('aria-labelledby');
  const label = id === null ? null : document.getElementById(id);
  if (label !== null && element.contains(label) && /^H[1-6]$/.test(label.tagName)) return label;
  if (element.hasAttribute('aria-label') || label !== null) return element;
  const control = element.querySelector<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])',
  );
  return control ?? element;
}

/**
 * Moves focus to the thing's heading or to the thing (focusable by script only, for as long as it's
 * shown), scrolls to it (at once when motion is reduced), and marks it shown. Returns false, and
 * does nothing, when it isn't on the page or can't be seen.
 */
export function showHelpAnchor(id: HelpAnchorId): boolean {
  const element = findHelpAnchor(id);
  if (element === null) return false;
  clearHelpShown();
  const target = focusTargetOf(element);
  const added = !target.hasAttribute('tabindex') && target.tabIndex < 0;
  if (added) target.setAttribute('tabindex', '-1');
  // Focus first: while it's still in Help the window has no padding for the Level 2 bar
  // (HelpPanel.module.css), and the scroll would stop under the bar (APP-141).
  target.focus({ preventScroll: true });
  if (!inView(element.getBoundingClientRect()) && typeof element.scrollIntoView === 'function')
    element.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  setHelpShown(id);
  const onFocusOut = (event: FocusEvent): void => {
    const next = event.relatedTarget;
    if (next instanceof Node && element.contains(next)) return;
    clearHelpShown();
  };
  element.addEventListener('focusout', onFocusOut);
  release = () => {
    element.removeEventListener('focusout', onFocusOut);
    if (added) target.removeAttribute('tabindex');
  };
  return true;
}
